import { ActionService } from '@acme/api';
import {
  $applicationLiquidityProviders,
  $liquidityProviders,
  applicationOpened,
  liquidityProvidersLoadRequested,
} from '@entities/application';
import type { DealParticipantRow } from '@widgets/providersTable';
import {
  combine,
  createEffect,
  createEvent,
  createStore,
  sample,
} from 'effector';

export const pendingAllocationSet = createEvent<{
  providerId: string;
  value: number;
}>();
export const pendingAllocationRemoved = createEvent<string>();
export const pendingAllocationsCleared = createEvent();
export const submitErrorSet = createEvent<string | null>();

export const submitOrderFx = createEffect(
  async (params: {
    actionId: string;
    applicationId: string;
    liquidityAllocations: Record<string, number>;
  }) => {
    const res = await ActionService.postApiV1ActionFormExchangeRates({
      id: params.actionId,
      requestBody: {
        comment: '',
        liquidityAllocations: params.liquidityAllocations,
      },
    });
    if (!res.isSuccess) {
      throw new Error(
        res.errorText ?? 'Не удалось оформить заказ. Попробуйте снова.'
      );
    }
    return { applicationId: params.applicationId };
  }
);

export const $pendingAllocations = createStore<Record<string, number>>({})
  .on(pendingAllocationSet, (state, { providerId, value }) => ({
    ...state,
    [providerId]: value,
  }))
  .on(pendingAllocationRemoved, (state, providerId) => {
    const next = { ...state };
    delete next[providerId];
    return next;
  })
  .on(pendingAllocationsCleared, () => ({}))
  .on(submitOrderFx.doneData, () => ({}));

export const $submitError = createStore<string | null>(null)
  .on(submitErrorSet, (_, error) => error)
  .on(submitOrderFx.done, () => null)
  .on(pendingAllocationSet, () => null)
  .on(pendingAllocationRemoved, () => null);

sample({
  clock: submitOrderFx.failData,
  fn: (error) =>
    error instanceof Error ? error.message : 'Не удалось оформить заказ',
  target: submitErrorSet,
});

export const $orderAmount = $pendingAllocations.map((alloc) =>
  Object.values(alloc).reduce((acc, cur) => acc + cur, 0)
);

export const $currentAmount = $applicationLiquidityProviders.map((providers) =>
  (providers ?? []).reduce<number>((acc, cur) => acc + (cur?.amount ?? 0), 0)
);

export const $isOrderDisabled = $pendingAllocations.map(
  (alloc) => Object.keys(alloc).length === 0
);

export const $dealParticipantRows = combine(
  $applicationLiquidityProviders,
  $pendingAllocations,
  $liquidityProviders,
  (providers, pendingAllocations, liquidityProviders): DealParticipantRow[] => {
    const serverRows: DealParticipantRow[] = (providers ?? []).map((p) => ({
      source: 'server' as const,
      data: p,
    }));
    const pendingRows: DealParticipantRow[] = Object.entries(pendingAllocations)
      .map(([providerId, amount]) => {
        const provider = liquidityProviders.find((p) => p.id === providerId);
        if (!provider) return null;
        const price =
          provider.ranges?.length && provider.ranges[0].price != null
            ? provider.ranges[0].price
            : 0;
        return {
          source: 'pending' as const,
          id: providerId,
          organizationName: provider.name ?? '',
          price,
          amount,
          sum: amount * price,
          provider,
        };
      })
      .filter((r): r is NonNullable<typeof r> => r != null);
    return [...serverRows, ...pendingRows];
  }
);

sample({
  clock: submitOrderFx.doneData,
  fn: ({ applicationId }) => ({ id: applicationId }),
  target: [applicationOpened, liquidityProvidersLoadRequested],
});
